/**
 * Promotional banner impressions.
 *
 * Pushes `promo_banner_displayed` once per banner, the first time at least half of it is on
 * screen. Nothing else on the site tracks impressions, so this is its own observer rather than a
 * reuse of the lazy-loading one.
 *
 * The payload is read from the banner's `data-analytics-payload`, built server-side by
 * PromotionalBannerViewModel.AnalyticsPayload, so displays and CTA clicks carry the same
 * banner_reference and CTR can be reported per campaign. CTA clicks are tracked declaratively by
 * analyticsEvents.js, not here.
 */
import { readPayload } from "./analyticsEvents";

const BANNER_SELECTOR = "[data-promotional-banner]";
const DISPLAYED_EVENT = "promo_banner_displayed";

function pushDisplayed(banner) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: DISPLAYED_EVENT, ...readPayload(banner) });
}

export default function initPromotionalBanners() {
  const banners = document.querySelectorAll(BANNER_SELECTOR);
  if (!banners.length) return;

  // No observer means no way to know what was seen; count on render rather than never.
  if (!("IntersectionObserver" in window)) {
    banners.forEach(pushDisplayed);
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      observer.unobserve(entry.target);
      pushDisplayed(entry.target);
    });
  }, { threshold: 0.5 });

  banners.forEach((banner) => observer.observe(banner));
}
